// src/components/layout/AdminLayout.tsx — Layout principal del panel admin
import { useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Sun, Moon, LogOut } from 'lucide-react'
import Sidebar from './Sidebar'
import { useAuthStore } from '@/store/authStore'
import CambiarContrasenaObligatorio from '@/components/auth/CambiarContrasenaObligatorio'

declare const __BUILD_DATE__: string

export default function AdminLayout() {
  const { user, logout } = useAuthStore()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    return (localStorage.getItem('pow-theme') as 'light' | 'dark') || 'light'
  })

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    localStorage.setItem('pow-theme', theme)
  }, [theme])

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light')
  }

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--color-surface-900)' }}>
      <Sidebar isDark={theme === 'dark'} />

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Topbar */}
        <header className="flex items-center justify-between px-6 py-4 flex-shrink-0"
                style={{ background: 'var(--color-surface-800)', borderBottom: '1px solid var(--color-border)' }}>
          
          <div className="text-xs text-[var(--color-text-secondary)] opacity-70 ml-2" id="versionTag" style={{ fontSize: '11px', opacity: 0.7 }}>
            Compilación: {__BUILD_DATE__}
          </div>

          <div className="flex items-center gap-3 ml-auto">
            {/* Theme Toggle */}
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-[var(--color-surface-700)] transition-colors cursor-pointer" title="Cambiar tema">
              {theme === 'dark' ? (
                <Sun size={18} style={{ color: 'var(--color-text-secondary)' }} />
              ) : (
                <Moon size={18} style={{ color: 'var(--color-text-secondary)' }} />
              )}
            </button>


            {/* Avatar & Dropdown */}
            <div className="relative">
              <div onClick={() => setDropdownOpen(!dropdownOpen)}
                   className="flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-[var(--color-surface-700)] select-none">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                     style={{ background: 'var(--color-miva-blue)', color: 'white' }}>
                  {user?.nombre?.[0] ?? 'A'}
                </div>
                <span className="text-sm hidden md:block" style={{ color: 'var(--color-text-secondary)' }}>
                  {user?.nombre ?? 'Admin'}
                </span>
              </div>

              {dropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setDropdownOpen(false)} />
                  <div className="absolute right-0 mt-2 w-48 rounded-xl bg-white dark:bg-[#0f213a] shadow-xl border border-gray-100 dark:border-gray-800 py-1 z-20 animate-fade-in text-[var(--color-text-primary)]">
                    <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800">
                      <p className="text-xs font-semibold truncate">{user?.nombre ?? 'Admin'}</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{user?.rol ?? 'Superadmin'}</p>
                    </div>
                    <button
                      onClick={() => {
                        setDropdownOpen(false)
                        logout()
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[var(--color-miva-rose)] hover:bg-rose-50 dark:hover:bg-rose-950/20 text-left transition-colors cursor-pointer"
                    >
                      <LogOut size={14} />
                      Cerrar sesión
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>

      {/* Cambio de contraseña obligatorio (LOGIN-15) */}
      <CambiarContrasenaObligatorio />
    </div>
  )
}
